/**
 * PAPPO — PermissionsPage
 * Aba "Permissões". Reúne:
 *  1. "Minha aprovação" — toggle liga/desliga da aprovação de ligações.
 *  2. "Preferências de aviso" — por filho, o responsável escolhe (checkboxes)
 *     de quais categorias de risco quer ser AVISADO. A IA continua analisando e
 *     registrando tudo; isto só filtra o que vira alerta. Aliciamento e
 *     automutilação são sempre ativos (trava de segurança, não desmarcáveis).
 */

import { useState, useEffect, useCallback } from 'react';
import { SlidersHorizontal, ShieldCheck, BellRing, Lock, Link2 } from 'lucide-react';
import {
  getParentSettings,
  setParentSettings,
  listChildren,
  getAlertPreferences,
  setAlertPreferences,
  setChildPermission,
  type Child,
  type AlertPreferences,
} from '../services/parent';
import styles from './PermissionsPage.module.css';

/** Categorias configuráveis (chave = valor em inglês do fan-out) + texto amigável. */
const EDITABLE_CATEGORIES: {
  key: keyof AlertPreferences;
  label: string;
  desc: string;
}[] = [
  { key: 'drugs', label: 'Drogas', desc: 'Menções a drogas, álcool, cigarro ou vape.' },
  { key: 'violence', label: 'Violência', desc: 'Ameaças, agressão física, armas ou brigas.' },
  { key: 'bullying', label: 'Bullying', desc: 'Ofensas, humilhação, exclusão ou intimidação.' },
  { key: 'scam', label: 'Golpe', desc: 'Pedidos de dinheiro, dados pessoais ou links suspeitos.' },
  { key: 'sexual_content', label: 'Sexual', desc: 'Conteúdo ou abordagem de teor sexual.' },
];

/** Categorias travadas (sempre ativas): os riscos mais graves. */
const LOCKED_CATEGORIES: { label: string; desc: string }[] = [
  { label: 'Aliciamento', desc: 'Adulto tentando manipular, pedir segredo ou marcar encontro.' },
  { label: 'Automutilação', desc: 'Autolesão, ideação suicida ou depressão grave.' },
];

const DEFAULT_PREFS: AlertPreferences = {
  drugs: true,
  violence: true,
  bullying: true,
  scam: true,
  sexual_content: true,
};

export function PermissionsPage() {
  const [approvalRequired, setApprovalRequired] = useState(true);
  const [children, setChildren] = useState<Child[]>([]);
  const [prefs, setPrefs] = useState<Record<number, AlertPreferences>>({});

  const load = useCallback(async () => {
    const settings = await getParentSettings();
    if (settings.success && settings.data)
      setApprovalRequired(settings.data.approval_required);

    const kids = await listChildren();
    if (kids.success && kids.data) {
      setChildren(kids.data.children);
      // Carrega as preferências de cada filho em paralelo.
      const entries = await Promise.all(
        kids.data.children.map(async (c) => {
          const res = await getAlertPreferences(c.id);
          return [c.id, res.success && res.data ? res.data.preferences : DEFAULT_PREFS] as const;
        })
      );
      setPrefs(Object.fromEntries(entries));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggleApproval = useCallback(async (value: boolean) => {
    setApprovalRequired(value);
    await setParentSettings(value);
  }, []);

  /* Convites por filho (F5): padrão desligado. Otimista, reverte se falhar. */
  const handleToggleInvite = useCallback(
    async (childId: number, value: boolean) => {
      setChildren((prev) =>
        prev.map((c) => (c.id === childId ? { ...c, can_invite: value } : c))
      );
      const res = await setChildPermission(childId, value);
      if (!res.success) {
        setChildren((prev) =>
          prev.map((c) => (c.id === childId ? { ...c, can_invite: !value } : c))
        );
      }
    },
    []
  );

  const handleToggleCategory = useCallback(
    async (childId: number, key: keyof AlertPreferences, value: boolean) => {
      const base = prefs[childId] ?? DEFAULT_PREFS;
      const next = { ...base, [key]: value };
      setPrefs((prev) => ({ ...prev, [childId]: next })); // otimista
      const res = await setAlertPreferences(childId, next);
      if (!res.success) {
        setPrefs((prev) => ({ ...prev, [childId]: base })); // reverte se falhar
      }
    },
    [prefs]
  );

  return (
    <div className={styles.page}>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.titleRow}>
          <SlidersHorizontal size={28} className={styles.titleIcon} />
          <div>
            <h1 className={styles.title}>Permissões</h1>
            <p className={styles.subtitle}>
              Sua aprovação e as suas preferências de aviso
            </p>
          </div>
        </div>
      </div>

      {/* Minha aprovação */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          <ShieldCheck size={16} style={{ verticalAlign: '-2px', marginRight: 6 }} />
          Minha aprovação
        </h2>
        <label className={styles.childRow} style={{ cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={approvalRequired}
            onChange={(e) => handleToggleApproval(e.target.checked)}
            style={{ width: 20, height: 20, accentColor: 'var(--accent-primary)' }}
          />
          <div className={styles.childInfo}>
            <span className={styles.childName}>Exigir a minha aprovação</span>
            <span className={styles.childUser}>
              Ligações que envolvem um filho seu só acontecem após a sua aprovação.
            </span>
          </div>
        </label>
      </section>

      {/* Convites de amizade (por filho) */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          <Link2 size={16} style={{ verticalAlign: '-2px', marginRight: 6 }} />
          Convites de amizade
        </h2>
        <p className={styles.hint}>
          Por padrão, seu filho <strong>não</strong> pode gerar links de convite.
          Ligue aqui para permitir que ele convide amigos (se a sua aprovação
          estiver ligada, você continua aprovando cada conexão).
        </p>

        {children.length === 0 && (
          <p className={styles.empty}>Você ainda não tem filhos vinculados.</p>
        )}

        {children.map((child) => (
          <label
            key={child.id}
            className={styles.childRow}
            style={{ cursor: 'pointer' }}
          >
            <input
              type="checkbox"
              checked={!!child.can_invite}
              onChange={(e) => handleToggleInvite(child.id, e.target.checked)}
              style={{ width: 20, height: 20, accentColor: 'var(--accent-primary)' }}
            />
            <div className={styles.childInfo}>
              <span className={styles.childName}>
                {child.display_name} — pode gerar convites
              </span>
              <span className={styles.childUser}>
                Quando ligado, o botão “Convidar amigo” aparece para {child.display_name}.
              </span>
            </div>
          </label>
        ))}
      </section>

      {/* Preferências de aviso */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          <BellRing size={16} style={{ verticalAlign: '-2px', marginRight: 6 }} />
          Preferências de aviso
        </h2>
        <p className={styles.hint}>
          Escolha de quais tipos de risco você quer ser avisado nas conversas de
          cada filho. A PAPPO continua analisando e registrando tudo — isto muda
          apenas quais situações geram um alerta para você. Aliciamento e
          automutilação, por serem os riscos mais graves, ficam sempre ativos.
        </p>

        {children.length === 0 && (
          <p className={styles.empty}>Você ainda não tem filhos vinculados.</p>
        )}

        {children.map((child) => {
          const childPrefs = prefs[child.id] ?? DEFAULT_PREFS;
          return (
            <div key={child.id} className={styles.childGroup}>
              <h3 className={styles.childGroupTitle}>{child.display_name}</h3>
              <div className={styles.children}>
                {EDITABLE_CATEGORIES.map((cat) => (
                  <label
                    key={cat.key}
                    className={styles.childRow}
                    style={{ cursor: 'pointer' }}
                  >
                    <input
                      type="checkbox"
                      checked={childPrefs[cat.key]}
                      onChange={(e) =>
                        handleToggleCategory(child.id, cat.key, e.target.checked)
                      }
                      style={{ width: 20, height: 20, accentColor: 'var(--accent-primary)' }}
                    />
                    <div className={styles.childInfo}>
                      <span className={styles.childName}>{cat.label}</span>
                      <span className={styles.childUser}>{cat.desc}</span>
                    </div>
                  </label>
                ))}

                {LOCKED_CATEGORIES.map((cat) => (
                  <label
                    key={cat.label}
                    className={styles.childRow}
                    style={{ cursor: 'default', opacity: 0.85 }}
                  >
                    <input
                      type="checkbox"
                      checked
                      disabled
                      style={{ width: 20, height: 20, accentColor: 'var(--accent-primary)' }}
                    />
                    <div className={styles.childInfo}>
                      <span className={styles.childName}>
                        {cat.label}
                        <span className={styles.badge}>
                          <Lock size={11} style={{ verticalAlign: '-1px', marginRight: 3 }} />
                          sempre ativo
                        </span>
                      </span>
                      <span className={styles.childUser}>{cat.desc}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
}
